/**
 * utils/fixedBidMarkup.ts — FRONTEND MIRROR of supabase/functions/server/fixedBidMarkupCore.ts.
 *
 * Generated: do not edit by hand; run scripts/sync-discovery-grid-mirror.sh.
 * tests/server/fixedBidMarkup_test.ts asserts parity.
 */


/** The highest markup a bid may carry: a guard against a mistyped figure, not a pricing policy. */
export const MARKUP_MAX_PERCENT = 100;

// Both branches name both fields so the frontend's non-strict tsconfig can read them without narrowing.
export type MarkupPercentCheck = { ok: true; percent: number; error?: never } | { ok: false; percent?: never; error: string };

/** A markup percentage as typed or stored: empty is 0; otherwise 0 to 100, kept to two decimals. */
export function parseMarkupPercent(value: unknown): MarkupPercentCheck {
  if (value === undefined || value === null || (typeof value === 'string' && value.trim() === '')) return { ok: true, percent: 0 };
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value.trim()) : NaN;
  if (!Number.isFinite(n)) return { ok: false, error: 'Markup must be a number' };
  if (n < 0) return { ok: false, error: 'Markup cannot be negative' };
  if (n > MARKUP_MAX_PERCENT) return { ok: false, error: `Markup is at most ${MARKUP_MAX_PERCENT}%` };
  return { ok: true, percent: Math.round(n * 100) / 100 };
}

/** A stored percent for display and defaults: 0 when it is missing or not valid. */
export function markupPercentOf(value: unknown): number {
  const r = parseMarkupPercent(value);
  return r.ok ? r.percent : 0;
}

/** A dollar figure rounded to whole dollars; 0 when it is missing, negative or not a number. */
export function wholeDollars(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? Math.round(n) : 0;
}

export interface FixedBidQuote {
  /** The bid before markup, in whole dollars. */
  base: number;
  percent: number;
  /** The markup in whole dollars. */
  markup: number;
  /** What the client pays: base plus markup. */
  total: number;
}

/** Base, markup and bid total in whole dollars; the markup rounds half up. */
export function fixedBidQuote(base: unknown, percent: unknown): FixedBidQuote {
  const b = wholeDollars(base);
  const p = markupPercentOf(percent);
  // In hundredths of a percent the product is an exact integer, so halves round up reliably.
  const markup = Math.round((b * Math.round(p * 100)) / 10000);
  return { base: b, percent: p, markup, total: b + markup };
}

/** Fixed bid under any of its stored names: the SOW's `fixed`, Sizing's `fixed_bid`, `fixed_price`. */
export function isFixedBidModel(model: unknown): boolean {
  const m = String(model ?? '').toLowerCase();
  return m === 'fixed' || m === 'fixed_bid' || m === 'fixed_price';
}

const MARKUP_FIELDS = ['basePrice', 'markupPercent', 'markupAmount'] as const;

export type PricingCheck = { ok: true; pricing: any; error?: never } | { ok: false; pricing?: never; error: string };

/**
 * An engagement's pricing as a route receives it (the SOW generator's `{ model, fixedPrice,
 * total }` or the proposal editor's `{ type, amount }`), with the markup settled:
 * - not a fixed bid: the markup fields are dropped (time and materials pays hours as worked);
 * - a fixed bid without `markupPercent` or `basePrice`: unchanged, as older writers send it;
 * - a fixed bid with either: the percent is validated, the base is `basePrice` (else the
 *   contract figure as sent), and `fixedPrice`, `amount` and `total` become the bid total.
 * Running it again on its own output gives the same pricing.
 */
export function withFixedBidMarkup(pricing: unknown): PricingCheck {
  if (!pricing || typeof pricing !== 'object' || Array.isArray(pricing)) return { ok: true, pricing };
  const p: Record<string, any> = { ...(pricing as Record<string, any>) };
  if (!isFixedBidModel(p.model ?? p.type)) {
    for (const k of MARKUP_FIELDS) delete p[k];
    return { ok: true, pricing: p };
  }
  if (p.markupPercent === undefined && p.basePrice === undefined) return { ok: true, pricing: p };
  const percent = parseMarkupPercent(p.markupPercent);
  if (!percent.ok) return { ok: false, error: percent.error };
  const rawBase = p.basePrice ?? p.fixedPrice ?? p.amount ?? p.total;
  if (rawBase !== undefined && rawBase !== null && rawBase !== '') {
    const n = Number(rawBase);
    if (!Number.isFinite(n) || n < 0) return { ok: false, error: 'The fixed bid price must be a number of dollars, 0 or more' };
  }
  const q = fixedBidQuote(rawBase, percent.percent);
  return { ok: true, pricing: { ...p, basePrice: q.base, markupPercent: q.percent, markupAmount: q.markup, fixedPrice: q.total, amount: q.total, total: q.total } };
}

/** The quote a stored pricing carries, or null unless it is a priced fixed bid with a markup above 0. */
export function storedFixedBidQuote(pricing: any): FixedBidQuote | null {
  if (!pricing || typeof pricing !== 'object' || !isFixedBidModel(pricing.model ?? pricing.type)) return null;
  const percent = markupPercentOf(pricing.markupPercent);
  if (percent <= 0) return null;
  const quote = fixedBidQuote(pricing.basePrice ?? pricing.fixedPrice ?? pricing.amount ?? pricing.total, percent);
  return quote.base > 0 ? quote : null;
}

/** The firm's default markup for new fixed bids (Firm › Settings › Scoping), 0 when unset. */
export function firmDefaultMarkupPercent(firm: any): number {
  return markupPercentOf(firm?.defaultFixedBidMarkupPercent);
}

/** What an engagement's SOW says about markup: whether it is a fixed bid, and its percent. */
export function engagementMarkup(engagement: any): { fixedBid: boolean; markupPercent: number } {
  const pricing = engagement?.pricing;
  const fixedBid = isFixedBidModel(pricing?.model ?? pricing?.type);
  return { fixedBid, markupPercent: fixedBid ? markupPercentOf(pricing?.markupPercent) : 0 };
}

export interface ChangeOrderPricing {
  basePrice: number;
  markupPercent: number;
  markupAmount: number;
  total: number;
  /** `sow`: the SOW's percent when the change order was saved; `override`: the change order's own. */
  markupSource: 'sow' | 'override';
}

export type ChangeOrderPricingCheck = { ok: true; pricing: ChangeOrderPricing | null; error?: never } | { ok: false; pricing?: never; error: string };

/**
 * A change order's price: a fixed amount plus markup. On a fixed-bid engagement the change order
 * takes the SOW's markup percent unless it names its own (`markupPercent` set); the percent is
 * copied onto the change order when it is saved, so a later change to the SOW never reprices it.
 * On any other engagement the markup is 0: the added hours are billed as worked. No `basePrice`
 * means no price (the change order reads as before).
 */
export function changeOrderPricing(input: unknown, engagement: any): ChangeOrderPricingCheck {
  if (input === undefined || input === null) return { ok: true, pricing: null };
  if (typeof input !== 'object' || Array.isArray(input)) return { ok: false, error: 'pricing must be an object' };
  const i = input as Record<string, any>;
  if (i.basePrice === undefined || i.basePrice === null || i.basePrice === '') return { ok: true, pricing: null };
  const n = Number(i.basePrice);
  if (!Number.isFinite(n) || n < 0) return { ok: false, error: 'The change order price must be a number of dollars, 0 or more' };
  const sow = engagementMarkup(engagement);
  let percent = sow.markupPercent;
  let source: ChangeOrderPricing['markupSource'] = 'sow';
  const overrides = i.markupPercent !== undefined && i.markupPercent !== null && i.markupPercent !== '';
  if (sow.fixedBid && overrides) {
    const r = parseMarkupPercent(i.markupPercent);
    if (!r.ok) return { ok: false, error: r.error };
    percent = r.percent;
    source = 'override';
  }
  const q = fixedBidQuote(n, percent);
  return { ok: true, pricing: { basePrice: q.base, markupPercent: q.percent, markupAmount: q.markup, total: q.total, markupSource: source } };
}
